<template>
  <div class="h-full">
    <el-scrollbar
      ref="scrollbarRef"
      v-loading="loading"
      element-loading-background="transparent"
      :style="`height: calc(100% - 28px);`"
    >
      <div class="text-[#344767] font-semibold text-[20px] pl-[24px] pb-[16px]">
        <span>{{ isCreate ? '' : '重新' }}申请个人用户</span>
      </div>

      <steps
        :steps="getFinancePersonalLianSteps()"
        :max-width="1400"
        :active-key="isSubmited ? ELianLianStepKey.Submit : currStepKey"
      />

      <!-- 上传营业执照 -->
      <div v-if="currStepKey === ELianLianStepKey.License" class="base-card-info">
        <div class="info-card-title">
          {{ currentName }}
        </div>

        <personal-info
          ref="personalInfoRef"
          :data="toPersonalCompStructure(info)"
          :disabled-fields="['idFrontImageUrl', 'idBackImageUrl', 'userName', 'idType', 'idNumber']"
          @key-enter="toNextStep"
        />
      </div>

      <!-- 上传开户绑卡信息 -->
      <template v-if="currStepKey === ELianLianStepKey.AccOpening">
        <div class="base-card-info">
          <div class="info-card-title">
            {{ currentName }}
          </div>

          <personal-bind-card
            ref="personalBindCardRef"
            :data="toBindBankStructure(info)"
            @key-enter="toNextStep"
          />
        </div>
      </template>

      <!-- 支付信息认证 -->
      <apply-submit-form
        v-if="currStepKey === ELianLianStepKey.Submit"
        ref="submitFormRef"
        random-path="pay/unified-lian-account/get-random-key"
        random-api-method="post"
        phone-desc="连连开户验证手机号，用于连连开户最后一步意愿确认使用"
        :revalid-fn="validFn"
        :re-valid="true"
        :send-sms="sendSms"
        :random-query-id="lianId"
        @submit="doSubmit"
      />

      <!-- 已提交 -->
      <div v-if="currStepKey === ELianLianStepKey.Submited" class="base-card-info">
        <account-apply-success
          class="submitted"
          :show-loading="false"
          :bank-info="bankInfo"
          @go-back="closeAndBack"
          @re-apply="() => replaceRoute({ step: ELianLianStepKey.License })"
        />
      </div>
    </el-scrollbar>

    <div v-if="!isSubmited" class="pt-[16px]">
      <el-button @click="closeAndBack">
        取消
      </el-button>
      <el-button v-if="prevStep" @click="toPrevStep">
        上一步
      </el-button>
      <el-button type="primary" :loading="saving" @click="toNextStep">
        {{ submitStep ? '提交' : '下一步' }}
      </el-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ESystemType } from '@core/api';
/* eslint-disable @stylistic/js/padding-line-between-statements */
import { Steps } from '@vue3/components';
import { computed, ref, toRaw } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElScrollbar, MessageHandler } from 'element-plus';
import { scrollToErrorField, toElScrollTop } from '@core/tools';
import { EAccountType, ELianLianStepKey, EUserType, IAgentMergeInfo, IFinanceLianDetail, ILianDetail } from '@core/main/types';
// eslint-disable-next-line @stylistic/js/max-len
import { getFinancePersonalLianStep, getFinancePersonalLianSteps, getFinancePersonalPrevLianStep, getMergeFinancePersonalLianInfo, queryAgentDetail, queryFinanceLianDetail, sendFinanceLianSmscode, submitFinanceLianLian, submitFinanceLianLicense, submitFinanceOpenparam, toFinanceLianPersonalStructure, toLinalianStructure, toPersonalCompStructure, toBindBankStructure, submitFinanceLianVerify } from '@core/main/api';
import { useTabsStore, useUserStore, IApplySubmitParam, AccountApplySuccess, ApplySubmitForm, PersonalInfo, PersonalBindCard } from '@core/main';

let tokenForVerify = '';
let tokenForSubmit = '';

defineOptions({ name: 'FinanceLianPersonalForm' }); // 资金账户 - 申请个体户连连户

const route = useRoute();
const router = useRouter();

const { closeAndBack, editTabName, updatetRouteQuery } = useTabsStore();
const { state: userState } = useUserStore();

const personalInfoRef = ref<InstanceType<typeof PersonalInfo>>();
const personalBindCardRef = ref<InstanceType<typeof PersonalBindCard>>();

const loading = ref(false);
const saving = ref(false);
const info = ref({} as IFinanceLianDetail);
const bankInfo = ref({} as ILianDetail);
const scrollbarRef = ref<InstanceType<typeof ElScrollbar>>();
const submitFormRef = ref<InstanceType<typeof ApplySubmitForm>>(null);

const lianId = computed(() => Number(route.query.lianId));
const isCreate = computed(() => route.query.create === '1');
const currStepKey = computed(() => route.query.step as ELianLianStepKey || ELianLianStepKey.License);
const prevStep = computed(() => getFinancePersonalPrevLianStep(currStepKey.value));
const submitStep = computed(() => currStepKey.value === ELianLianStepKey.Submit);
const isSubmited = computed(() => currStepKey.value === ELianLianStepKey.Submited);
const currentName = computed(() => getFinancePersonalLianStep(currStepKey.value)?.title);
const identity = computed(() => userState.identity);

const userType = computed(() => {
  if (identity.value.systemType === ESystemType.Agent) return EUserType.AGENT;
  if (identity.value.systemType === ESystemType.Server) return EUserType.SERVER;
  if (identity.value.systemType === ESystemType.Company) return EUserType.COMPANY;

  return undefined;
});

async function getAgentDetail() {
  let agent = {} as IAgentMergeInfo;
  try {
    agent = await queryAgentDetail(identity.value.agentId);
  } catch (error) {
    //
  }
  return agent;
}

async function fetchLianDetail() {
  loading.value = true;
  const agent = await getAgentDetail();

  if (!lianId.value) { // 新建开户
    info.value = getMergeFinancePersonalLianInfo(agent);
    loading.value = false;
    return;
  }

  try { // 重新申请
    const lianInfo = await queryFinanceLianDetail(lianId.value);
    info.value = getMergeFinancePersonalLianInfo(agent, lianInfo);

    bankInfo.value = toLinalianStructure(lianInfo);
  } catch (error) {
    //
  }
  loading.value = false;
}

async function sendSms(phone: string) {
  tokenForVerify = await sendFinanceLianSmscode(phone, lianId.value);
}

async function validFn(valid = false) {
  const values = submitFormRef.value?.getValues();

  tokenForSubmit = await submitFinanceLianLian({
    lianId: lianId.value,
    token: tokenForVerify,
    regPhone: values.phone,
    verifyCode: values.code,
    password: values.password,
    randomKey: submitFormRef.value?.getRandomKeys()?.randomKey,
  });
}

async function doSubmit(values: IApplySubmitParam) {
  saving.value = true;
  try {
    await submitFinanceLianVerify({
      lianId: lianId.value,
      verifyCode: values.verifyCode,
      token: tokenForSubmit,
      password: values.password,
      randomKey: submitFormRef.value?.getRandomKeys()?.randomKey,
    });

    bankInfo.value = await queryFinanceLianDetail(lianId.value);
    replaceRoute({ step: ELianLianStepKey.Submited });
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  saving.value = false;
}

async function savePersonalnfo() {
  const valid = await personalInfoRef.value?.validate();
  if (!valid) return;

  saveHandler(
    async () => {
      const vals = toFinanceLianPersonalStructure(personalInfoRef.value?.getValues());

      const d = await submitFinanceLianLicense({
        ...getLianCodes(),
        ...vals,
        userType: userType.value,
        accountType: EAccountType.PERSONAL_PAYMENT,
      });
      const pushId = d.lianId || lianId.value || undefined;

      if (!pushId) return ElMessage.error('服务端未返回lianId，请检查');

      info.value = {
        ...toRaw(info.value),
        ...vals,
        lianId: pushId,
      };

      replaceRoute({
        lianId: pushId,
        step: ELianLianStepKey.AccOpening,
      });
    },
  );
}

async function saveBankCardInfo() {
  const bankCardValid = await personalBindCardRef.value?.validate();
  if (!bankCardValid) return;

  saveHandler(
    async () => {
      const d = toRaw(personalBindCardRef.value?.getValues());
      const bankNo = d.bankAccountNumber;
      const linkedPhone = d.bankReservedPhone;

      await submitFinanceOpenparam(
        {
          ...getLianCodes(),
          bankNo,
          linkedPhone,
        },
        true,
      );

      info.value = {
        ...toRaw(info.value),
        bankNo,
        linkedPhone,
      };

      replaceRoute({
        step: ELianLianStepKey.Submit,
        reservedPhone: info.value.linkedPhone,
      });
    },
  );
}

function getLianCodes() {
  return { lianId: lianId.value ? lianId.value : undefined };
}

function replaceRoute(v: Record<string, any>) {
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      ...v,
    },
  });
  updatetRouteQuery({ ...v });
  toElScrollTop();
}

function handleSubmit() {
  submitFormRef.value?.validateForm(
    doSubmit,
    () => {
      scrollToErrorField(submitFormRef.value?.$el);
    },
  );
}

async function saveHandler(callback: () => Promise<void | MessageHandler>) {
  saving.value = true;

  try {
    await callback();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  saving.value = false;
}

function toPrevStep() {
  const prevStep = getFinancePersonalPrevLianStep(currStepKey.value);

  replaceRoute({ step: prevStep.key });
}

function toNextStep() {
  if (submitStep.value) return handleSubmit();

  switch (currStepKey.value) {
    case ELianLianStepKey.License:
      savePersonalnfo();
      break;
    case ELianLianStepKey.AccOpening:
      saveBankCardInfo();
      break;
    default:
      break;
  }
}

fetchLianDetail();

editTabName(`${isCreate.value ? '' : '重新'}申请连连户`);
</script>

<style lang="scss" scoped>
:deep(.box-content) {
  padding: 50px 0 80px;
}

.base-card-info {
  min-height: calc(100vh - 393px);

  &.c-auto {
    min-height: unset;
  }
}

.submitted {
  min-height: calc(100vh - 420px) !important;
}

:deep(.steps-items-box) {
  min-height: unset !important;
}
</style>
